import React, { useState } from 'react'
import { colors } from '@atlaskit/theme'
import { getRecentUpdates, RecentCard, RecentPage } from '../services/recentUpdates'

// Status author objects matching homepage (Trello.tsx)
const todo = {
  id: '1',
  name: 'To Do',
  colors: {
    soft: colors.Y200,
    hard: colors.Y200,
  },
}

const inProgress = {
  id: '2',
  name: 'In Progress',
  colors: {
    soft: colors.B200,
    hard: colors.G200,
  },
}

const done = {
  id: '3',
  name: 'Done',
  colors: {
    soft: colors.G200,
    hard: colors.B200,
  },
}

// Get author object based on status
const getAuthorByStatus = (status: string) => {
  switch (status) {
    case 'todo':
      return todo
    case 'in-progress':
    case 'inprogress':
      return inProgress
    case 'done':
      return done
    default:
      return todo
  }
}

// Transform API card data to match component expected format
const transformCard = (card: RecentCard): any => ({
  ...card,
  board_name: card.boardName,
  board_column_id: card.columnId,
  // Add author object with colors matching homepage
  author: getAuthorByStatus(card.status),
  // Map assignees to the format expected by removeDuplication/capitalizeEachWord
  assignees: card.assignees?.map((a) => a.fullName) || [],
  // Keep original assignee data for clone functionality
  assigneeBoardPersonIds: card.assignees?.map((a) => ({ boardPersonId: a.board_person_id })) || [],
  // Map reminders to card_reminders (home page expects card_reminders)
  card_reminders: card.reminders || [],
})

// Transform API page data to match component expected format
const transformPage = (page: RecentPage): any => ({
  ...page,
  // Ensure boardName is trimmed and within 100 char limit
  boardName: page.boardName?.trim()?.substring(0, 100) || '',
  board: { name: page.boardName?.trim()?.substring(0, 100) || '' },
  // EditPageModal expects board_people with user.fullName structure
  // Ensure file_attachments is in correct format (array of objects, not strings)
  file_attachments: Array.isArray(page.file_attachments)
    ? page.file_attachments.map((file: any, index: number) =>
        typeof file === 'string' ? { [`file_${index}`]: file } : file
      )
    : [],
  // Ensure these fields exist for EditPageModal
  socialLinks: (page as any).socialLinks || '',
  otherLinks: (page as any).otherLinks || '',
})

const useGetRecentUpdates = (): {
  recentTasks: any[]
  recentPages: any[]
  setRecentTasks: React.Dispatch<React.SetStateAction<any[]>>
  setRecentPages: React.Dispatch<React.SetStateAction<any[]>>
  loadingTasks: boolean
  loadingPages: boolean
  fetchRecentUpdates: () => Promise<void>
} => {
  const [recentTasks, setRecentTasks] = useState<any[]>([])
  const [recentPages, setRecentPages] = useState<any[]>([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [loadingPages, setLoadingPages] = useState(false)

  const fetchRecentUpdates = async () => {
    setLoadingTasks(true)
    setLoadingPages(true)
    try {
      const res = await getRecentUpdates()
      const cardsData = res.data?.data?.cards || []
      const pagesData = res.data?.data?.pages || []

      // Transform and set tasks
      const transformedTasks = Array.isArray(cardsData)
        ? cardsData.map(transformCard)
        : []
      setRecentTasks(transformedTasks)

      // Transform and set pages
      const transformedPages = Array.isArray(pagesData)
        ? pagesData.map(transformPage)
        : []
      setRecentPages(transformedPages)
    } catch {
      setRecentTasks([])
      setRecentPages([])
    } finally {
      setLoadingTasks(false)
      setLoadingPages(false)
    }
  }

  return {
    recentTasks,
    recentPages,
    setRecentTasks,
    setRecentPages,
    loadingTasks,
    loadingPages,
    fetchRecentUpdates,
  }
}

export default useGetRecentUpdates
